import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const POLL_INTERVAL_MS = 10_000;
const STALE_THRESHOLD_MS = 2 * 60 * 60 * 1000; // ignore jobs older than 2 hours

interface Result {
  isProcessing: boolean;
  hasFailed: boolean;
}

export function useFinancialSnapshotProcessing(
  organisationId: string | undefined,
  onComplete: () => void,
): Result {
  const wasProcessingRef = useRef(false);

  const { data } = useQuery<Result>({
    queryKey: ["financial-snapshot-jobs", organisationId],
    enabled: Boolean(organisationId),
    refetchInterval: (query) => (query.state.data?.isProcessing ? POLL_INTERVAL_MS : false),
    queryFn: async (): Promise<Result> => {
      const since = new Date(Date.now() - STALE_THRESHOLD_MS).toISOString();
      const { data: rows, error } = await supabase
        .from("financial_snapshot_jobs")
        .select("status")
        .eq("organisation_id", organisationId!)
        .gte("created_at", since);

      if (error) {
        console.warn("[financial-snapshot-jobs] query error:", error.message);
        return { isProcessing: false, hasFailed: false };
      }

      const jobs = rows ?? [];
      const isProcessing = jobs.some((r) => r.status === "running");
      const hasAnySuccess = jobs.some((r) => r.status === "success");
      const hasFailed = !isProcessing && !hasAnySuccess && jobs.some((r) => r.status === "failed");
      return { isProcessing, hasFailed };
    },
  });

  const isProcessing = data?.isProcessing ?? false;

  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      onComplete();
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing, onComplete]);

  return data ?? { isProcessing: false, hasFailed: false };
}
